// Slice 8.1 — Create a CRM contact from a "new lead" conversation.
// Pre-fills name from conversation, infers phone/email from preview/handle,
// posts to /contacts, then links it back to the conversation.
import { useEffect, useState } from "react";
import { UserPlus, Loader2, Phone, Mail } from "lucide-react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_OPTIONS = ["New", "Active PRO", "Active Trial", "Churned"];

function guessFromConversation(conv) {
  // Try to extract a phone or email from preview/contact_name
  const text = `${conv?.contact_name || ""} ${conv?.preview || ""}`;
  const phoneMatch = text.match(/\+?\d[\d\s\-()]{6,}/);
  const emailMatch = text.match(/[\w.+-]+@[\w-]+\.[\w.-]+/);
  // Prefer the authoritative WhatsApp number (WATI) / stored handle when present.
  const waPhone = conv?.wa_id ? `+${String(conv.wa_id).replace(/\D/g, "")}` : "";
  const handlePhone = /^\+?\d[\d\s\-()]{6,}$/.test(conv?.from_handle || "")
    ? conv.from_handle.replace(/[^\d+]/g, "") : "";
  // Don't seed the name from an auto "Outbound <number>" / bare-number label.
  const rawName = conv?.contact_name || "";
  const looksLikeNumber = /^(\+?\d[\d\s\-()]*|Outbound\s+\+?\d[\d\s\-()]*)$/i.test(rawName.trim());
  return {
    full_name: looksLikeNumber ? "" : rawName,
    phone: waPhone || handlePhone || (phoneMatch ? phoneMatch[0].replace(/[^\d+]/g, "") : ""),
    email: emailMatch ? emailMatch[0] : "",
  };
}

export default function CreateLeadFromConvDialog({ conversation, onClose, onCreated }) {
  const [form, setForm] = useState({
    full_name: "", phone: "", email: "", company: "", designation: "",
    city: "", state: "", status: "New", notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!conversation) return;
    const g = guessFromConversation(conversation);
    setForm((f) => ({
      ...f,
      full_name: g.full_name || f.full_name,
      phone:     g.phone     || f.phone,
      email:     g.email     || f.email,
      notes:     conversation.preview ? `First message: "${conversation.preview}"` : "",
    }));
  }, [conversation?.id]);

  async function save() {
    if (!form.full_name.trim()) { toast.error("Full name is required"); return; }
    setBusy(true);
    try {
      const { data: contact } = await api.post("/contacts", {
        full_name: form.full_name.trim(),
        phone:    form.phone.trim(),
        email:    form.email.trim(),
        company:  form.company.trim(),
        designation: form.designation.trim(),
        city:     form.city.trim(),
        state:    form.state.trim(),
        status:   form.status,
      });
      // Link the conversation to the new contact so the rest of the workspace
      // (CRM panel, screen-pop, callbacks) recognises it.
      if (conversation?.id) {
        try {
          await api.patch(`/conversations/${conversation.id}/link-contact`, {
            contact_id: contact.id,
          });
        } catch { /* backend may not have this endpoint yet — non-fatal */ }
      }
      if (form.notes) {
        try { await api.post(`/contacts/${contact.id}/comments`, { body: form.notes }); } catch { /* */ }
      }
      onCreated?.(contact);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent data-testid="create-lead-dialog" className="max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-primary" /> Create CRM Contact
          </DialogTitle>
          <DialogDescription>
            Convert this lead into a CRM contact. Once saved, the conversation will be
            linked and the agent can place voice calls, log dispositions, and view history.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label className="text-xs">Full Name *</Label>
              <Input data-testid="lead-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Customer name" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Phone className="w-3 h-3"/> Phone</Label>
              <Input data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-1"><Mail className="w-3 h-3"/> Email</Label>
              <Input data-testid="lead-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="customer@email" />
            </div>
            <div>
              <Label className="text-xs">Company</Label>
              <Input data-testid="lead-company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">Designation</Label>
              <Input data-testid="lead-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">City</Label>
              <Input data-testid="lead-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs">State</Label>
              <Input data-testid="lead-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </div>
          </div>
          <div>
            <Label className="text-xs">First-touch note</Label>
            <Textarea data-testid="lead-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Captured from the inbound message…" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button data-testid="lead-save" onClick={save} disabled={busy} className="gap-1.5">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            Create &amp; link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
